// Screener-ul pe telefon: o listă de monede şi un grafic pe tot ecranul.
//
// Tiparul e cel din TradingView pe telefon, şi e cel corect: pe un ecran de şase ţoli
// nu încap şi lista, şi graficul, şi panourile laterale. Deci sunt două ecrane. În
// primul cauţi, în al doilea te uiţi — iar din al doilea te întorci cu un buton, nu cu
// un gest ghicit.
//
// Graficul primeşte tot ce rămâne după antet şi bara de taimfreimuri. Uneltele de desen
// apar doar când le ceri şi dispar când termini: un grafic acoperit pe jumătate de
// butoane nu mai e un grafic.

import { el, clear, bus, fmtPrice, fmtPct, fmtVolume, debounce } from '../core/util.js';
import { icon } from '../ui/icons.js';
import { iconMoneda } from '../ui/coin-icon.js';
import { market, settings } from '../core/state.js';
import { sparkline, toast } from '../ui/components.js';
import { foaie } from '../ui/sheet.js';
import { contextMenu } from '../ui/components.js';
import { Chart } from '../chart/chart.js';
import { DrawingLayer } from '../chart/drawings.js';
import { VizorButon } from '../chart/vizor-buton.js';
import { TIMEFRAMES } from '../core/exchanges.js';

/** Câte monede ţinem în listă. Mai jos de-atât nimeni nu derulează pe telefon. */
const CATE = 120;

/**
 * Felurile de sortare.
 *
 * Fiecare arată în rând **exact cifra după care sortează** — altfel lista se rearanjează
 * şi nu se vede de ce. `a doua linie` e ce scrie sub ticker în locul volumului.
 */
const SORTARI = [
  { id: 'volum', eticheta: 'Объём', cheie: (r) => r.quoteVolume || 0,
    linie: (r) => fmtVolume(r.quoteVolume) },
  { id: 'crestere', eticheta: 'Рост', cheie: (r) => r.change24h ?? -999,
    linie: (r) => fmtVolume(r.quoteVolume) },
  { id: 'cadere', eticheta: 'Падение', cheie: (r) => -(r.change24h ?? 999),
    linie: (r) => fmtVolume(r.quoteVolume) },
  { id: 'volatil', eticheta: 'Волатильность', cheie: volatilitate,
    linie: (r) => (r.natr ? `ATR ${r.natr.toFixed(2)}%`
      : `диапазон ${volatilitate(r).toFixed(1)}%`) },
  { id: 'aflux', eticheta: 'Всплеск объёма', cheie: (r) => r.volTrend || 0,
    linie: (r) => `объём ×${(r.volTrend || 0).toFixed(1)}`,
    // ATR-ul si aflux-ul se calculeaza pe server intr-un pas separat, care nu e gata
    // imediat dupa pornire. Volatilitatea are cu ce sa se descurce fara el — diapazonul
    // zilei —, afluxul nu are. Un chip care nu poate sorta dupa ce promite nu trebuie
    // sa stea pe ecran: mai bine lipseste decat sa reaseze lista la intamplare.
    disponibil: (lista) => lista.some((r) => r.volTrend) },
];

/**
 * Cat de mult se misca moneda, in procente.
 *
 * ATR-ul normalizat e masura buna, dar vine din pasul de calcul al serverului si poate
 * lipsi. Atunci folosim diapazonul zilei, care e mereu acolo: mai grosier, dar despre
 * acelasi lucru — si spunem in rand care dintre ele s-a folosit.
 */
function volatilitate(r) {
  if (r.natr) return r.natr;
  if (r.high24h && r.low24h) return ((r.high24h - r.low24h) / r.low24h) * 100;
  return 0;
}

/** Uneltele de desen care au sens pe un ecran mic. Restul rămân pe calculator. */
const UNELTE = [
  // Cursorul primul: cu el se citeste graficul — arata pretul de sub deget si deschide
  // ce se poate face cu el. Linia libera vine imediat dupa, fiind cea mai desenata; se
  // trage dintr-o miscare, de unde vrei pana unde vrei.
  { id: 'cursor', ico: 'cursor', titlu: 'Курсор' },
  { id: 'trend', ico: 'trend', titlu: 'Линия' },
  { id: 'hline', ico: 'hline', titlu: 'Горизонталь' },
  { id: 'ray', ico: 'ray', titlu: 'Луч' },
  { id: 'rect', ico: 'rect', titlu: 'Прямоугольник' },
  { id: 'fib', ico: 'fib', titlu: 'Фибоначчи' },
  { id: 'ruler', ico: 'ruler', titlu: 'Линейка' },
  { id: 'eraser', ico: 'eraser', titlu: 'Ластик' },
];

export class MobileScreenerSection {
  constructor(app) {
    this.app = app;
    this.cautare = '';
    this.sortare = settings.get('sx_mob_sort', 'volum');
    this.simbol = new URLSearchParams(location.search).get('symbol')
      || settings.get('sx_coins_symbol', '');
    this.tf = settings.get('sx_modal_tf', '5m');
    this.unsubs = [];
  }

  mount(host) {
    this.root = el('div', { class: 'ms-section' });
    host.append(this.root);

    // Dacă se ştie deja o monedă (link, alegere de dinainte), sărim direct la grafic:
    // omul care a apăsat pe SOL vrea SOL, nu lista din care l-a ales acum un minut.
    if (this.simbol) this.#deschideGrafic(this.simbol);
    else this.#lista();

    this.unsubs.push(bus.on('market:aggregates', debounce(() => {
      if (!this.graficDeschis) this.#randeaza();
      else this.#pretDinCap();
    }, 400)));
    this.unsubs.push(bus.on('market:sparklines', () => { if (!this.graficDeschis) this.#randeaza(); }));
  }

  // ------------------------------------------------------------------ lista

  #lista() {
    this.graficDeschis = false;
    this.#opresteDesenul();
    clear(this.root);

    const camp = el('input', {
      class: 'ms-cauta', type: 'search', placeholder: 'Монета, например SOL',
      value: this.cautare,
      oninput: debounce((e) => { this.cautare = e.target.value.trim().toUpperCase(); this.#randeaza(); }, 150),
    });

    this.sortEl = el('div', { class: 'ms-sort' });
    this.listaEl = el('div', { class: 'ms-lista' });
    this.root.append(
      el('div', { class: 'ms-cap' }, [
        el('div', { class: 'ms-cauta-cutie' }, [icon('search', 14), camp]),
        this.sortEl,
      ]),
      this.listaEl,
    );
    this.#randSortari();
    this.#randeaza();
  }

  #randSortari() {
    clear(this.sortEl);
    const lista = market.list();
    for (const s of SORTARI) {
      if (s.disponibil && !s.disponibil(lista)) continue;
      this.sortEl.append(el('button', {
        class: `ms-sort-b${s.id === this.sortare ? ' on' : ''}`, text: s.eticheta,
        onclick: () => {
          this.sortare = s.id;
          settings.set('sx_mob_sort', s.id);
          this.#randSortari();
          this.#randeaza();
          // Ducem derularea sus, nu lista in dreptul ei: `scrollIntoView` impingea
          // exact bara de sortari afara din ecran, adica butonul tocmai apasat.
          this.root.closest('.mt-view')?.scrollTo({ top: 0 });
        },
      }));
    }
  }

  #randeaza() {
    if (!this.listaEl) return;
    const toate0 = market.list();
    let fel = SORTARI.find((s) => s.id === this.sortare) || SORTARI[0];
    if (fel.disponibil && !fel.disponibil(toate0)) fel = SORTARI[0];
    const toate = toate0
      .filter((r) => r.quoteVolume)
      .sort((a, b) => fel.cheie(b) - fel.cheie(a));
    const lista = (this.cautare
      ? toate.filter((r) => r.symbol.includes(this.cautare))
      : toate).slice(0, CATE);

    clear(this.listaEl);
    if (!lista.length) {
      this.listaEl.append(el('div', { class: 'fd-gol-mic',
        text: market.list().length ? 'Ничего не нашлось' : 'Загружаем рынок…' }));
      return;
    }

    for (const r of lista) {
      const sus = (r.change24h || 0) >= 0;
      const spark = market.sparklines.get(r.symbol);
      this.listaEl.append(el('button', {
        class: 'ms-rand', onclick: () => this.#deschideGrafic(r.symbol),
      }, [
        iconMoneda(r.symbol, 30),
        el('div', { class: 'ms-rand-stanga' }, [
          el('span', { class: 'ms-tick', text: r.symbol.replace(/USDT$/, '') }),
          el('span', { class: 'ms-vol', text: fel.linie(r) }),
        ]),
        spark ? el('span', { class: 'ms-spark' }, [sparkline(spark, { width: 56, height: 22 })]) : null,
        el('div', { class: 'ms-rand-dreapta' }, [
          el('span', { class: 'ms-pret', text: fmtPrice(r.price) }),
          el('span', { class: `ms-schimb ${sus ? 'sus' : 'jos'}`, text: fmtPct(r.change24h) }),
        ]),
      ]));
    }
  }

  // ----------------------------------------------------------------- grafic

  async #deschideGrafic(simbol) {
    this.graficDeschis = true;
    this.simbol = simbol;
    settings.set('sx_coins_symbol', simbol);
    clear(this.root);

    this.pretEl = el('span', { class: 'mg-pret' });
    this.schimbEl = el('span', { class: 'mg-schimb' });

    const corpGrafic = el('div', { class: 'mg-corp' });
    this.uneltEl = el('div', { class: 'mg-unelte-bara' });
    this.uneltEl.style.display = 'none';

    this.creionEl = el('button', { class: 'mt-btn', title: 'Рисовать',
      onclick: () => this.#comutaDesenul() }, [icon('penRuler', 16)]);

    this.root.append(
      el('header', { class: 'mg-cap' }, [
        el('button', { class: 'mt-btn', title: 'Назад', onclick: () => this.#lista() },
          [icon('chevronLeft', 17)]),
        iconMoneda(simbol, 26),
        el('div', { class: 'mg-nume' }, [
          el('span', { class: 'mg-tick', text: simbol.replace(/USDT$/, '') }),
          el('span', { class: 'mg-piata', text: this.app.market === 'futures' ? 'FUTURES' : 'SPOT' }),
        ]),
        el('div', { class: 'mg-cifre' }, [this.pretEl, this.schimbEl]),
        // Taimfreimul intr-un buton, nu intr-un rand de sase: randul manca cincizeci de
        // pixeli din inaltimea graficului ca sa arate mereu cinci optiuni pe care nu le
        // apesi. Butonul spune care e ales si le deschide pe celelalte la nevoie.
        this.tfEl = el('button', { class: 'mg-tf-buton', onclick: (e) => this.#meniuTf(e) }),
        this.creionEl,
        el('button', { class: 'mt-btn', title: 'Ещё', onclick: () => this.#foaieUnelte() },
          [icon('ellipsis', 16)]),
      ]),
      this.uneltEl,
      corpGrafic,
    );

    this.#randTf();
    this.#pretDinCap();

    this.chart?.destroy();
    this.chart = new Chart(corpGrafic, {
      exchange: this.app.exchange,
      market: this.app.market,
      symbol: simbol,
      tf: this.tf,
      indicators: settings.get('sx_indicators', { volume: true }),
      // Mai puţine lumânări decât pe calculator: pe un ecran de telefon, o mie de
      // lumânări sunt o mie de dungi de un pixel. Trei sute se şi văd, şi se încarcă
      // de trei ori mai repede.
      limit: 300,
    });
    await this.chart.setSymbol(simbol, { exchange: this.app.exchange, market: this.app.market });

    // Stratul de desen e acelaşi ca pe calculator — figurile desenate pe telefon se
    // văd şi acolo, fiindcă se salvează la fel.
    this.desene = new DrawingLayer(this.chart, this.chart.mainEl);
    this.desene.onToolDone = () => this.#dupaDesen();
    this.desene.onAlertToggle = (d) => this.#alertaPeLinie(d);

    // „+"-ul de pe crosshair. Sta acolo din capul locului, ca in TradingView: nu ceri
    // voie ca sa te uiti la un pret. Se ascunde doar cat sunt deschise uneltele, unde
    // degetul are alta treaba.
    this.vizorButon?.destroy();
    this.vizorButon = new VizorButon(this.chart, (pret) => this.#meniuVizor(pret));
    this.desene.onAlertRemoved = async (d) => {
      await fetch(`/api/alerts/${d.alertId}/delete`, { method: 'POST' }).catch(() => {});
      bus.emit('alerts:changed');
    };
    this.desene.load();
  }

  #randTf() {
    if (!this.tfEl) return;
    clear(this.tfEl).append(
      el('span', { text: this.tf }),
      icon('chevronDown', 10),
    );
  }

  #meniuTf(ev) {
    contextMenu(ev.currentTarget, TIMEFRAMES.map((tf) => ({
      label: tf === this.tf ? `${tf}  ✓` : tf,
      onClick: async () => {
        this.tf = tf;
        settings.set('sx_modal_tf', tf);
        this.#randTf();
        await this.chart?.setTimeframe(tf);
        this.desene?.render();
      },
    })));
  }

  // ------------------------------------------------------------- desenul

  #comutaDesenul() {
    const deschis = this.uneltEl.style.display !== 'none';
    if (deschis) { this.#opresteDesenul(); return; }
    this.uneltEl.style.display = '';
    this.creionEl.classList.add('activ');
    this.vizorButon?.setAscuns(true);
    this.#randUnelte();
  }

  #opresteDesenul() {
    if (!this.uneltEl) return;
    this.uneltEl.style.display = 'none';
    this.creionEl?.classList.remove('activ');
    this.alerteaza = false;
    this.desene?.setTool('cursor');
    this.vizorButon?.setAscuns(false);
  }

  #randUnelte() {
    clear(this.uneltEl);
    const activa = this.desene?.tool || 'cursor';

    for (const u of UNELTE) {
      this.uneltEl.append(el('button', {
        class: `mg-unealta${activa === u.id && !this.alerteaza ? ' on' : ''}`, title: u.titlu,
        onclick: () => {
          this.alerteaza = false;
          this.desene?.setTool(u.id);
          this.#randUnelte();
        },
      }, [icon(u.ico, 16)]));
    }

    // Alarma nu e o unealtă de desen aparte: e o linie orizontală care, după ce o pui,
    // se înregistrează şi pe server. Aşa nu trebuie să înveţi două gesturi pentru
    // acelaşi lucru — desenezi nivelul, iar el te şi anunţă când preţul îl atinge.
    this.uneltEl.append(
      el('span', { class: 'mg-unelte-sep' }),
      el('button', {
        class: `mg-unealta alarma${this.alerteaza ? ' on' : ''}`, title: 'Линия-алерт',
        onclick: () => {
          this.alerteaza = !this.alerteaza;
          this.desene?.setTool(this.alerteaza ? 'hline' : 'cursor');
          this.#randUnelte();
          if (this.alerteaza) toast('Коснитесь графика на нужном уровне', 'info');
        },
      }, [icon('bell', 15)]),
      el('button', { class: 'mg-unealta', title: 'Снимок графика',
        onclick: async () => {
          const { faCaptura, pastreaza, inClipboard } = await import('../ui/captura.js');
          const url = faCaptura(this.chart, this.desene);
          if (!url) { toast('Не удалось снять график', 'error'); return; }
          pastreaza(url, this.simbol);
          await inClipboard(url);
          toast('Снимок готов — прикрепите к публикации', 'success');
        } }, [icon('camera', 15)]),
      el('button', { class: 'mg-unealta', title: 'Отменить',
        onclick: () => { this.desene?.undo(); } }, [icon('undo', 15)]),
      el('button', { class: 'mg-unealta', title: 'Убрать всё',
        onclick: () => {
          if (!confirm('Убрать все рисунки с этого графика?')) return;
          this.desene?.clearAll();
        } }, [icon('trash', 15)]),
    );
  }

  /** După ce s-a terminat o figură: dacă era o alarmă, o trimitem şi pe server. */
  #dupaDesen() {
    const lista = this.desene?.drawings || [];
    const ultima = lista[lista.length - 1];
    if (this.alerteaza && ultima) {
      this.alerteaza = false;
      this.#alertaPeLinie(ultima);
    }
    this.desene?.setTool('cursor');
    this.#randUnelte();
  }

  /**
   * Ce se poate face cu preţul arătat de crosshair.
   *
   * Două lucruri, atât: un nivel care te anunţă şi o linie pe care o vezi. TradingView
   * mai pune acolo trei rânduri de tranzacţionare — cumpără, vinde, ordin — dar noi nu
   * ducem ordine la bursă, iar un rând care nu face nimic e mai rău decât unul lipsă.
   *
   * O foaie de jos, nu un meniu lângă deget: preţul e sus, în dreapta, iar un meniu
   * deschis acolo ar cere mâna întinsă peste tot ecranul. Foaia vine la deget.
   */
  #meniuVizor(pret) {
    const simbol = (this.simbol || '').replace(/USDT$/, '');
    const text = fmtPrice(pret);

    const rand = (iconita, titlu, laClic) => el('button', {
      class: 'vz-rand', onclick: () => { inchide(); laClic(); },
    }, [
      el('span', { class: 'vz-icon' }, [icon(iconita, 17)]),
      el('span', { class: 'vz-text' }, [
        el('span', { class: 'vz-titlu', text: titlu }),
        el('span', { class: 'vz-pret', text: `на ${text}` }),
      ]),
    ]);

    const { inchide } = foaie({
      corp: el('div', { class: 'vz-lista' }, [
        rand('bell', `Добавить оповещение для ${simbol}`,
          () => this.#alertaLaPret(pret)),
        rand('hline', 'Нарисовать горизонтальную линию',
          () => this.#liniePret(pret)),
      ]),
    });
  }

  /** Linie orizontala la pretul aratat, fara sa mai fie desenata cu mana. */
  #liniePret(pret, tacut = false) {
    const bare = this.chart?.bars || [];
    const cand = bare.length ? bare[Math.max(0, bare.length - 20)].time : Math.floor(Date.now() / 1000);
    const desen = {
      id: `d_${Date.now().toString(36)}`,
      tool: 'hline',
      points: [{ time: cand, price: pret }],
      color: this.desene.color,
      width: this.desene.style.width,
      opacity: this.desene.style.opacity,
    };
    this.desene.drawings.push(desen);
    this.desene.save();
    this.desene.render();
    if (!tacut) toast('Линия поставлена', 'success');
    return desen;
  }

  /** Nivel-alerta la pretul aratat: o linie care te si anunta. */
  async #alertaLaPret(pret) {
    const desen = this.#liniePret(pret, true);
    await this.#alertaPeLinie(desen);
  }

  /** Pune sau scoate alerta de pe o linie desenată. */
  async #alertaPeLinie(desen) {
    if (desen.alertId) {
      await fetch(`/api/alerts/${desen.alertId}/delete`, { method: 'POST' }).catch(() => {});
      delete desen.alertId;
      this.desene.save();
      this.desene.render();
      toast('Уровень снят');
      bus.emit('alerts:changed');
      return;
    }
    const pret = desen.points?.[0]?.price;
    if (!pret) return;
    try {
      const resp = await fetch('/api/alerts/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symbol: this.simbol, price: pret, exchange: this.app.exchange,
          market: this.app.market, direction: 'cross',
        }),
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.detail || resp.statusText);
      desen.alertId = d.alert?.id ?? d.id;
      this.desene.save();
      this.desene.render();
      toast(`Уровень ${fmtPrice(pret)} поставлен`, 'success');
      bus.emit('alerts:changed');
    } catch (err) {
      toast('Не удалось поставить уровень', 'error');
      console.warn('[alert]', err);
    }
  }

  /** Preţul din antet, luat din instantaneul pieţei — nu cere nimic în plus. */
  #pretDinCap() {
    const r = market.rows.get(this.simbol);
    if (!r || !this.pretEl) return;
    const sus = (r.change24h || 0) >= 0;
    this.pretEl.textContent = fmtPrice(r.price);
    this.schimbEl.textContent = fmtPct(r.change24h);
    this.schimbEl.className = `mg-schimb ${sus ? 'sus' : 'jos'}`;
  }

  /** Cifrele monedei şi indicatorii, ca foaie. */
  #foaieUnelte() {
    const ind = settings.get('sx_indicators', { volume: true });
    const r = market.rows.get(this.simbol) || {};

    const comutator = (cheie, eticheta) => el('label', { class: 'mg-comut' }, [
      el('span', { text: eticheta }),
      el('input', {
        type: 'checkbox', checked: !!ind[cheie],
        onchange: (e) => {
          const nou = { ...settings.get('sx_indicators', { volume: true }), [cheie]: e.target.checked };
          settings.set('sx_indicators', nou);
          this.chart?.setIndicators(nou);
        },
      }),
    ]);

    const corp = el('div', { class: 'mg-unelte' }, [
      el('div', { class: 'mg-detalii' }, [
        this.#cifra('Цена', fmtPrice(r.price)),
        this.#cifra('24ч', fmtPct(r.change24h)),
        this.#cifra('Объём', fmtVolume(r.quoteVolume)),
        this.#cifra('ATR', r.natr ? `${r.natr.toFixed(2)}%` : '—'),
      ]),
      el('div', { class: 'mg-unelte-titlu', text: 'Индикаторы' }),
      comutator('volume', 'Объём'),
      comutator('rsi', 'RSI'),
      comutator('cvd', 'CVD'),
      el('div', { class: 'mg-unelte-titlu', text: 'Действия' }),
      el('button', {
        class: 'mt-meniu-rand',
        onclick: async () => {
          const { faCaptura, pastreaza, inClipboard } = await import('../ui/captura.js');
          const url = faCaptura(this.chart, this.desene);
          if (!url) { toast('Не удалось снять график', 'error'); return; }
          pastreaza(url, this.simbol);
          await inClipboard(url);
          toast('Снимок готов — прикрепите его к публикации или сообщению', 'success');
        },
      }, [icon('camera', 15), el('span', { text: 'Снимок графика' })]),
      el('button', {
        class: 'mt-meniu-rand',
        onclick: () => { location.href = `/chat/?symbol=${encodeURIComponent(this.simbol)}`; },
      }, [icon('comments', 15), el('span', { text: 'Обсуждение монеты' })]),
      el('button', {
        class: 'mt-meniu-rand',
        onclick: () => {
          navigator.clipboard?.writeText(
            `${location.origin}/screener/coins/?symbol=${this.simbol}`);
          toast('Ссылка скопирована', 'success');
        },
      }, [icon('share', 15), el('span', { text: 'Скопировать ссылку' })]),
    ]);

    foaie({ titlu: this.simbol, corp });
  }

  #cifra(eticheta, valoare) {
    return el('div', { class: 'mg-detaliu' }, [
      el('span', { class: 'mg-detaliu-e', text: eticheta }),
      el('span', { class: 'mg-detaliu-v', text: valoare }),
    ]);
  }

  unmount() {
    for (const off of this.unsubs) off();
    this.desene?.destroy();
    this.chart?.destroy();
    this.root?.remove();
  }
}
